import React, { useState, useEffect } from 'react';
import { UI, useTimeMachine } from 'react-time-machine-js';

export const STORAGE_KEY = 'backend-time-settings';

const _formatDateUTC = (ts) => {
    const d = new Date(ts);
    const yyyy = d.getUTCFullYear();
    const MM = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const HH = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    return `${yyyy}/${MM}/${dd} ${HH}:${mm}`;
};

const _parseDate = (str) => {
    if (!str) return NaN;
    const match = String(str).trim().match(/^(\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2})$/);
    if (!match) return NaN;
    const [_, y, M, d, H, m] = match;
    return new Date(y, M - 1, d, H, m).getTime();
};

export const computeBackendTs = (displayTime, settings = {}) => {
    if (settings.manualMode && settings.manualTime) {
        const parsed = _parseDate(settings.manualTime);
        if (!isNaN(parsed)) return parsed;
    }
    return displayTime;
};

export const setBackendHeader = (displayTime, settings) => {
    const ts = computeBackendTs(displayTime, settings);
    window.__timeMachineBackendTime = new Date(ts).toISOString();
};

export const clearBackendHeader = () => {
    delete window.__timeMachineBackendTime;
};

const BackendTimePanel = () => {
    const { displayTime, active, store } = useTimeMachine();
    const [manualMode, setManualMode] = useState(false);
    const [manualTime, setManualTime] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const saved = store.get(STORAGE_KEY);
        if (saved) {
            setManualMode(saved.manualMode ?? false);
            setManualTime(saved.manualTime ?? '');
        }
        setIsInitialized(true);
    }, [store]);

    useEffect(() => {
        if (!isInitialized) return;
        store.set(STORAGE_KEY, { manualMode, manualTime });

        if (active) setBackendHeader(displayTime, { manualMode, manualTime });
    }, [manualMode, manualTime, store, isInitialized, active, displayTime]);

    const backendTimestamp = active
        ? computeBackendTs(displayTime, { manualMode, manualTime })
        : null;

    const isValidManual = manualMode ? !isNaN(_parseDate(manualTime)) : true;
    const displayBackendTime = backendTimestamp && !isNaN(backendTimestamp)
        ? _formatDateUTC(backendTimestamp)
        : (active ? (manualMode ? 'Fecha inválida' : 'Calculando...') : 'Reloj inactivo');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <UI.Label>Hora del servidor (Backend - UTC)</UI.Label>

            <div style={{
                padding: '10px',
                background: 'rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: '6px',
            }}>
                <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Hora enviada en cabecera:
                </div>
                <div style={{
                    fontSize: '15px',
                    fontWeight: 'bold',
                    fontFamily: 'monospace',
                    color: !isValidManual && manualMode ? '#d32f2f' : 'inherit'
                }}>
                    {displayBackendTime}
                </div>
            </div>

            <UI.Divider />

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                    type="checkbox"
                    id="tm-manual-mode"
                    checked={manualMode}
                    onChange={e => setManualMode(e.target.checked)}
                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <UI.Label htmlFor="tm-manual-mode" style={{ cursor: 'pointer', margin: 0, fontWeight: 'medium' }}>
                    Modo manual
                </UI.Label>
            </div>

            {manualMode && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                    <UI.Input
                        value={manualTime}
                        onChange={e => setManualTime(e.target.value)}
                        placeholder="2024/01/01 12:00"
                    />
                    <div style={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
                        Formato requerido: yyyy/MM/dd HH:mm
                    </div>
                </div>
            )}

            {!active && (
                <div style={{ fontSize: '11px', color: '#f57c00', marginTop: '4px' }}>
                    ⚠ Activa el TimeMachine para enviar esta hora al backend.
                </div>
            )}
        </div>
    );
};

export const backendTimePlugin = {
    name: 'Hora del servidor',
    panel: () => <BackendTimePanel />,
};
